import StateObserver from 'src/StateObserver';
import { ChannelProperty, EntryProperties } from 'src/lib/analytics/properties';
import {
  trackNativeCrossplayFinish,
  trackNativeCrossplayStart,
  trackWebCrossplayFinish,
} from './crossplay.tracking';
import { isPlatformNative, openingLink } from 'src/lib/native/native.util';
import { guessInstalledNativeAppPlatform } from '../native/native.util';
import * as Native from 'src/replicant/native';
import { delay, withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { duration } from 'src/replicant/utils/duration';
import type {
  CrossPlayStartParams,
  CrossplayFinishTrackingWithTarget,
} from './crossplay.types';
import { GCInstant } from 'src/lib/gcinstant';
import { assertNever } from 'src/replicant/utils';
import { track } from 'src/lib/analytics/utils';

/**
 * Native app launch mutex, see startNativeCrossplaySequence
 */
let currentAppLaunchRequest: Promise<unknown> | undefined;

/**
 * Try to open native app.
 * Nothing will happen if it's uninstalled.
 */
export async function startNativeCrossplay(opts: {
  payload?: Partial<EntryProperties>;
  onBeforeSwitch?: (deepLink: string) => void;
  tracking: CrossPlayStartParams;
  shouldPreserveChannel?: boolean;
}): Promise<boolean> {
  // Tracking with target
  const tracking = {
    ...opts.tracking,
    target: 'native',
  } as const;

  // Can't open native app if we're already using it, or it's not installed
  if (isPlatformNative() || !guessInstalledNativeAppPlatform()) return false;

  // Don't allow multiple concurrent app launch attempts
  if (currentAppLaunchRequest) return false;

  // Determine deep link with payload
  const switchPayload = {
    ...(opts.payload || {}),
    ...GCInstant.crossplay.getCrossplayPayload({
      ...tracking,
      triggerSource: `${tracking.origin}_install`,
      wasInBackground: false,
      isInferred: false,
    }),
  };

  // Preserve channel if requested, otherwise default to CROSSPLAY.
  if (!opts.shouldPreserveChannel) {
    switchPayload.$channel = 'CROSSPLAY' as ChannelProperty;
  }

  const switchPayloadText = encodeURIComponent(JSON.stringify(switchPayload));
  const deepLink = `${Native.ruleset.nativeAppLaunchUrl}?payload=${switchPayloadText}`;

  // Invoke hook before app switch
  opts.onBeforeSwitch?.(deepLink);

  // Make sure to flush replicant before launching the app.
  await withLoading(() => StateObserver.flush(), {
    feature: Feature.NATIVE._,
    subFeature: Feature.NATIVE.REPLICANT_FLUSH,
  });

  // Make sure no other request is in progress.
  if (currentAppLaunchRequest) return false;

  // Launch native app and wait for it to switch
  currentAppLaunchRequest = new Promise<void>((resolve) => {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        resolve();
      }
    });
    window.location.assign(deepLink);
  });

  // Close window after app switch
  currentAppLaunchRequest.then(() => {
    trackNativeCrossplayStart(tracking); // only send the event if there really was a crossplay switch initiated

    if (window.liff?.isInClient()) {
      window.liff?.closeWindow();
    } else {
      window.close();
    }
  });

  return await Promise.race([
    // Return true if the app switch is detected
    currentAppLaunchRequest.then(() => true),

    // Return false if the app switch is not detected within 1 second
    delay(duration({ seconds: 1 })).then(() => false),
  ])
    // Allow subsequent app launch attempts after the current one resolves
    .finally(() => (currentAppLaunchRequest = undefined));
}

/**
 * Completes the crossplay entry process by performing necessary tracking.
 *
 * This function performs two main tasks:
 * 1. It waits for the `openingLink` promise to resolve and then tracks the login crossplay switch using the URL length.
 * 2. It retrieves the crossplay tracking data from `GCInstant.entryData` and tracks the completion of the crossplay process.
 *
 * @remarks
 * - The `openingLink` promise is expected to resolve with a URL string.
 * - The `GCInstant.entryData.crossplayTracking` object is used for tracking purposes.
 */
export function finishCrossplayEntry() {
  const crossplayTracking = (GCInstant.entryData.crossplayTracking ||
    {}) as CrossplayFinishTrackingWithTarget;

  if ('target' in crossplayTracking) {
    switch (crossplayTracking.target) {
      case 'native':
        openingLink.then((url) =>
          track('NativeLoginCrossplaySwitch', { deepLinkLength: url?.length }),
        );
        trackNativeCrossplayFinish({ ...crossplayTracking, target: 'native' });
        break;
      case 'web':
        openingLink.then((url) =>
          track('WebLoginCrossplaySwitch', { deepLinkLength: url?.length }),
        );
        trackWebCrossplayFinish({ ...crossplayTracking, target: 'web' });
        break;
      default:
        assertNever(crossplayTracking.target);
    }
  } else {
    // Default to previous behavior
    openingLink.then((url) =>
      track('NativeLoginCrossplaySwitch', { deepLinkLength: url?.length }),
    );
    trackNativeCrossplayFinish(crossplayTracking);
  }
}
